/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { Node } from '@msdyn365-commerce-modules/utilities';
import { IMfrmCompleteYourBedViewProps } from './mfrm-complete-your-bed';
import SelectableCard from '../../themes/mattressfirm/views/components/selectable-card';
import { renderBaseImage } from './util/renderBaseImage';
import { hasDiscount } from './util/hasDiscount';
import { getFallbackImageUrl } from '../../data-actions/utilities/utils';
import Price from '../../themes/mattressfirm/views/components/price/price.component';
import { BVRatingSummary } from '../../Utilities/components/BVRatingSummary';
import { renderRating } from '../../Utilities/get-rating';

export default (props: IMfrmCompleteYourBedViewProps) => {
    const { items, protectorData, onSelect, onUnselect, onSelectProtector, onUnselectProtector, protectorSelected, pillowSelected } = props;
    const renderBadge = (outOfStock: boolean | undefined, text: string | undefined) => {
        if (outOfStock) {
            return <Node className='complete-your-bed__oos_badge'>{text}</Node>;
        }
        return null;
    };
    const renderCard = items.map((item, i) => {
        const onchange = (e: boolean | React.ChangeEvent<HTMLInputElement> | any) => {
            const checkbox = e.target;
            const checkboxIsChecked = checkbox.checked;
            if (checkboxIsChecked === true) {
                onSelect(item, i, item.inventoryStatus?.outOfStock);
            } else {
                onUnselect(item, i);
            }
        };

        const htmlCheckboxId = "mfrm-complete-your-bed-checkbox-pillow";
        return (
            <>
                {renderBadge(item.inventoryStatus?.outOfStock, props.resources.outOfStockText)}
                <input
                    id={htmlCheckboxId}
                    className={"mfrm-complete-your-bed-checkbox"}
                    data-test-id="mfrm-complete-your-bed-checkbox-product-1"
                    type='checkbox'
                    checked={pillowSelected}
                    aria-checked={pillowSelected}
                    onChange={(e) => onchange(e)}
                    disabled={item.inventoryStatus?.outOfStock}
                />
                <label
                    htmlFor={htmlCheckboxId}
                >
                    <SelectableCard
                        img={renderBaseImage(
                            props.context.request.gridSettings,
                            item.PrimaryImageUrl,
                            item.productName,
                            getFallbackImageUrl(item.ItemId, props.context.request.apiSettings)
                        )}
                        eyebrowText={''}
                        productName={item.productName ? `${item.brand} ${item.productName}` : ''}
                        ratings={props.config.showBVRatings ? <BVRatingSummary itemId={item.ItemId} /> : renderRating(
                            props.context,
                            props.typeName,
                            props.id,
                            item.AverageRating,
                            item.TotalRatings,
                            props.resources.ratingAriaLabel
                        )}
                        standardPrice={
                            typeof item.CustomerContextualPrice === 'number' &&
                            typeof item.DiscountAmount === 'number' &&
                            item.CustomerContextualPrice + item.DiscountAmount
                        }
                        isActive={!item.inventoryStatus?.outOfStock}
                        currentPrice={hasDiscount(item) && item.CustomerContextualPrice} />
                </label>
                {pillowSelected && <span className='added-lable'>Added!</span>}
            </>);
    });

    const renderProtectorCard = protectorData && protectorData.map((protector, i) => {
        const onchange = (e: boolean | React.ChangeEvent<HTMLInputElement> | any) => {
            const checkbox = e.target;
            const checkboxIsChecked = checkbox.checked;
            if (checkboxIsChecked === true) {
                onSelectProtector(protector, i, protector?.inventoryStatus?.outOfStock);
            } else {
                onUnselectProtector(protector, i);
            }
        };

        const htmlCheckboxId = "mfrm-complete-your-bed-checkbox-protector";
        return (
            <>
                {renderBadge(protector?.inventoryStatus?.outOfStock, props.resources.outOfStockText)}
                <input
                    id={htmlCheckboxId}
                    className={"mfrm-complete-your-bed-checkbox"}
                    data-test-id="mfrm-complete-your-bed-checkbox-product-2"
                    type='checkbox'
                    checked={protectorSelected}
                    aria-checked={protectorSelected}
                    onChange={(e) => onchange(e)}
                    disabled={protector?.inventoryStatus?.outOfStock}
                />
                <label
                    htmlFor={htmlCheckboxId}
                >
                    <div className={`selectable-card ${protector?.inventoryStatus?.outOfStock ? 'selectable-card__inactive' : ''}`}>
                        <div className='selectable-card__image'>{renderBaseImage(
                            props.context.request.gridSettings,
                            protector.PrimaryImageUrl,
                            protector.productName,
                            getFallbackImageUrl(protector.ItemId, props.context.request.apiSettings)
                        )}</div>
                        <div className='selectable-card__content'>
                            <div className='selectable-card__eyebrow'>{''}</div>
                            <div className='selectable-card__product-name'>{protector.productName ? protector.brand ? `${protector.brand} ${protector.productName}` : `${protector.productName}` : ''}</div>
                            {protector.TotalRatings !== undefined && protector.AverageRating !== undefined && (
                                <div className='selectable-card__rating'>
                                    {props.config.showBVRatings ? (
                                        <BVRatingSummary itemId={protector.ItemId} />
                                    ) : (
                                        <span>
                                            {renderRating(
                                                props.context,
                                                props.typeName,
                                                props.id,
                                                protector.AverageRating,
                                                protector.TotalRatings,
                                                props.resources.ratingAriaLabel
                                            )}
                                        </span>
                                    )}
                                </div>
                            )}
                            {protector.CustomerContextualPrice && <Price standard={
                                typeof protector.CustomerContextualPrice === 'number' &&
                                    typeof protector.DiscountAmount === 'number' ?
                                    protector.CustomerContextualPrice + protector.DiscountAmount : 0
                            } current={hasDiscount(protector) && protector.CustomerContextualPrice} />}
                        </div>
                    </div>
                </label>
                {protectorSelected && <span className='added-lable'>Added!</span>} </>
        );
    });

    return (
        (items.length !== 0 || protectorData.length !== 0) && < div className='mfrm-complete-your-bed' >

            <p className='complete-your-bed__title'>{props.config.titleHeadingTag ? props.config.titleHeadingTag : "Complete Your Bed"}</p>
            {
                items.length !== 0 && <div className='complete-your-bed__content'>
                    {renderCard}
                </div>
            }
            {
                protectorData.length !== 0 && <div className='complete-your-bed__protector'>
                    {renderProtectorCard}
                </div>
            }
        </div >
    );
};
